import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { Form, Input, Modal, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import configuration from 'modules/configuration'
import { notifyMappingError } from '../../../utils'

const PLIKA_ADMINISTRATION = 'PLIKA'
const SAP_ADMINISTRATION = 'SAP'
const SUBTYPE_SOURCE = 'subtipo'

const DimensionMappingModal = ({
  visible,
  onClose,
  onRefetch,
  dimensions,
  usedDimensions,
  mapping,
  createDimensionMapping,
  editDimensionMapping,
}) => {
  const [form] = Form.useForm()
  const [isSaving, setIsSaving] = useState(false)
  const [sourceDimension, setSourceDimension] = useState(null)
  const { t } = useTranslation()

  const isEdit = Boolean(mapping)

  const initialValues = useMemo(
    () => ({
      name: mapping?.name,
      source_dimension: mapping?.source_dimension,
      targets: mapping?.targets ? [...mapping.targets] : undefined,
      source_system: mapping?.source_system ?? PLIKA_ADMINISTRATION,
    }),
    [mapping],
  )

  useEffect(() => {
    if (!visible) return
    form.setFieldsValue(initialValues)
    setSourceDimension(initialValues.source_dimension ?? null)
  }, [visible, form, initialValues])

  const sourceOptions = isEdit
    ? [
        {
          value: mapping.source_dimension,
          label: mapping.labels?.[mapping.source_dimension] ?? mapping.source_dimension,
        },
      ]
    : [
        ...dimensions.map((dimension) => ({
          value: dimension.name,
          label: dimension.name_destination,
        })),
        { value: SUBTYPE_SOURCE, label: t('CONFIG_DIMENSION_MAPPING_SUBTYPE_SOURCE') },
      ].filter((option) => !usedDimensions.includes(option.value))

  const targetOptions = dimensions
    .filter((dimension) => dimension.name !== sourceDimension)
    .map((dimension) => ({ value: dimension.name, label: dimension.name_destination }))

  const administrationOptions = [
    { value: PLIKA_ADMINISTRATION, label: t('CONFIG_DIMENSION_MAPPING_ADMINISTRATION_PLIKA') },
    ...(sourceDimension === SUBTYPE_SOURCE
      ? []
      : [{ value: SAP_ADMINISTRATION, label: SAP_ADMINISTRATION }]),
  ]

  const handleClose = () => {
    form.resetFields()
    setSourceDimension(null)
    setIsSaving(false)
    onClose()
  }

  const handleSubmit = () => {
    form.validateFields().then(({ source_system, source_dimension, ...values }) => {
      const data = {
        ...values,
        source_system: source_system === PLIKA_ADMINISTRATION ? null : source_system,
      }

      setIsSaving(true)
      const request = isEdit
        ? editDimensionMapping(mapping.source_dimension, data)
        : createDimensionMapping({ ...data, source_dimension })

      request
        .then(() => {
          onRefetch()
          handleClose()
        })
        .catch((error) => {
          setIsSaving(false)
          notifyMappingError(error)
        })
    })
  }

  return (
    <Modal
      title={t(
        isEdit
          ? 'CONFIG_DIMENSION_MAPPINGS_EDIT_MODAL_TITLE'
          : 'CONFIG_DIMENSION_MAPPINGS_CREATE_MODAL_TITLE',
      )}
      visible={visible}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={t(isEdit ? 'ACTION_SAVE' : 'ACTION_CREATE')}
      cancelText={t('ACTION_CANCEL')}
      confirmLoading={isSaving}
      destroyOnClose
      centered
      forceRender
    >
      <Form form={form} layout="vertical" initialValues={initialValues}>
        <Form.Item
          name="name"
          label={t('FIELD_NAME')}
          rules={[{ required: true, message: t('REQUIRED_FIELD') }]}
        >
          <Input placeholder={t('CONFIG_DIMENSION_MAPPINGS_CREATE_MODAL_TITLE')} />
        </Form.Item>
        <Form.Item
          name="source_dimension"
          label={t('CONFIG_DIMENSION_MAPPING_FIELD_SOURCE')}
          extra={!isEdit && t('CONFIG_DIMENSION_MAPPING_FIELD_SOURCE_HELP')}
          rules={[{ required: true, message: t('REQUIRED_FIELD') }]}
        >
          <Select
            options={sourceOptions}
            placeholder={t('ACTION_SELECT')}
            disabled={isEdit}
            onChange={(value) => {
              setSourceDimension(value)
              form.setFieldsValue({
                targets: [],
                ...(value === SUBTYPE_SOURCE && { source_system: PLIKA_ADMINISTRATION }),
              })
            }}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          name="targets"
          label={t('CONFIG_DIMENSION_MAPPING_FIELD_TARGETS')}
          extra={
            isEdit && mapping.rows_count > 0 && t('CONFIG_DIMENSION_MAPPING_FIELD_TARGETS_HELP')
          }
          rules={[{ required: true, message: t('REQUIRED_FIELD') }]}
        >
          <Select
            mode="multiple"
            options={targetOptions}
            placeholder={t('ACTION_SELECT')}
            disabled={!sourceDimension}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          name="source_system"
          label={t('CONFIG_DIMENSION_MAPPING_FIELD_ADMINISTRATION')}
          extra={t('CONFIG_DIMENSION_MAPPING_FIELD_ADMINISTRATION_HELP')}
          rules={[{ required: true, message: t('REQUIRED_FIELD') }]}
        >
          <Select options={administrationOptions} placeholder={t('ACTION_SELECT')} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

const mapDispatchToProps = {
  createDimensionMapping: configuration.actions.createDimensionMapping,
  editDimensionMapping: configuration.actions.editDimensionMapping,
}

export default connect(null, mapDispatchToProps)(DimensionMappingModal)
